import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import store from '@/store';
import router from '@/router';

type ErrorHandlers = { [key: number]: () => void };

declare module 'axios' {
  export interface AxiosInstance {
    request<T = any>(config: AxiosRequestConfig): Promise<T>;
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    post<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig
    ): Promise<T>;
    put<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig
    ): Promise<T>;
    patch<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig
    ): Promise<T>;
  }
}

export abstract class HttpClient {
  protected readonly client: AxiosInstance;

  protected constructor(baseURL: string, headers?: any) {
    this.client = axios.create({ baseURL, headers });

    this.initializeResponseInterceptor();
  }

  private initializeResponseInterceptor = () => {
    this.client.interceptors.response.use(
      this.handleResponse,
      this.handleError
    );

    this.client.interceptors.request.use(this.handleRequests, this.handleError);
  };

  private handleRequests = (request: AxiosRequestConfig) => {
    request.headers.token =
      window.localStorage.getItem('simulated-token') ||
      window.localStorage.getItem('token');
    return request;
  };

  private handleResponse = ({ data }: AxiosResponse) => {
    return data;
  };


  protected handleError = (error: AxiosError) => {
    const errorHandlers: ErrorHandlers = {
      401: () => this.handleUnauthorized(error),
      403: this.handleForbidden,
      409: () => this.handleConflict(error),
    };

    if (
      error.response &&
      Object.keys(errorHandlers).includes(error.response.status.toString())
    ) {
      errorHandlers[error.response.status]();
    } else {
      store.commit('showAlert', { message: 'Ocorreu um erro', type: 'danger' });
    }

    return Promise.reject(error);
  };

  private handleUnauthorized = ({ response }: AxiosError) => {
    if (response?.data) {
      const { data: error } = response;
      if (error?.name === 'UnregisteredUserError') {
        store.dispatch('logout');
        router.push({ name: 'userNotAuthorized' }).catch(() => undefined);
        store.commit('showAlert', {
          message: `
            Você ainda não tem acesso ao Monitor de marcas.
            <a href="${process.env.VUE_APP_MOMA_REGISTER_REQUEST_URL}">
               Clique aqui para solicitar.
            </a>
          `,
          type: 'danger',
          timeout: 300,
        });
        return;
      }

      if (error?.name === 'TokenError') {
        store.dispatch('logout');
        router.push({ name: 'login' }).catch(() => undefined);
        store.commit('showAlert', {
          message: `
            ${error?.message || 'Token inválido.'}
            <a href="/login">Faça login novamente.</a>
          `,
          type: 'warning',
          timeout: 300,
        });
        return;
      }

      router.push({ name: 'login' }).catch(() => undefined);
      store.commit('showAlert', {
        message:
          'Acesso não autorizado. Dúvidas entre em contato com o administrador.',
        type: 'danger',
        timeout: 300,
      });
      return;
    }

    store.dispatch('logout');
    router.push({ name: 'login' }).catch(() => undefined);
  };

  private handleConflict = ({ response }: AxiosError) => {
    if (response?.data) {
      const error = response.data;
      if (error?.name === 'AlreadyExistsError') {
        store.commit('showAlert', {
          message: 'Já existe um registro com esses dados.',
          type: 'danger',
          timeout: 300,
        });
        return;
      }
    }
    if (response?.data) {
      const error = response.data;
      if (error?.name === 'CannotDeleteDashboardError') {
        store.commit('showAlert', {
          message:
            'Não é possível deletar este dashboard enquanto houver filhos associados.',
          type: 'danger',
          timeout: 300,
        });
        return;
      }
    }
  };

  private handleForbidden = () => {
    store.commit('showAlert', {
      message: 'Você não tem permissão para acessar esse conteúdo',
      type: 'danger',
    });
  };
}
